/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { type AxiosInstance } from 'axios';
import {
  type GCPProductFinderResponseModel,
  type GCPProductFinderOptions,
  type GCPProductFinderInfo,
} from '../../productListingPage/interface';
import { getAxios } from '@/lib/axios';
import { requestBaseURLGCPExtOrInt } from '@/config/serviceAPI';
import { createQuery } from '@/utils/createReactQuery';
import { type QueryClient } from '@tanstack/react-query';

const URL = `${requestBaseURLGCPExtOrInt}/sls/b2c/product-discovery-product-data/v1/find-product`;

const getProductFinder = async (
  options: GCPProductFinderOptions,
  axiosInstance?: AxiosInstance
): Promise<GCPProductFinderInfo[]> => {
  const response = await getAxios(axiosInstance).get<GCPProductFinderResponseModel>(URL, {
    params: options,
    withCredentials: false,
  });
  return productFinderSelector(response.data);
};

const productFinderSelector = (data: GCPProductFinderResponseModel) => {
  const attributesList = data.vehicleProductAttributes;
  const selectedAttributes = attributesList?.map((selectedAttribute) => {
    return {
      attribute: selectedAttribute.vehicleProductAttributeName,
      value: selectedAttribute.vehicleProductAttributeValue,
      description: selectedAttribute.vehicleProductAttributeValueNote,
    };
  });

  return selectedAttributes ?? [];
};

const { useData: useProductFinderInfo, prefetch: prefetchProductFinderInfo } = createQuery<
  GCPProductFinderInfo[],
  GCPProductFinderOptions
>('product-finder', async (options, axiosInstance) =>
  getProductFinder({ ...options }, axiosInstance)
);

export const useGetProductFinder = (props: {
  country: string;
  vehicleId: string;
  productId: string;
  enabled: boolean;
}) => {
  const { country, vehicleId, productId, enabled } = props;
  const options = {
    country,
    vehicleId,
    productId,
    enabled: enabled && !!vehicleId && !!productId,
  };

  return useProductFinderInfo(options);
};

export const prefetchProductFinder = async (
  axiosInstance: AxiosInstance,
  queryClient: QueryClient,
  options: GCPProductFinderOptions
) => {
  return prefetchProductFinderInfo(queryClient, options, axiosInstance);
};
